import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import AdSlot from '../components/AdSlot';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const FadeSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'}
      variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut', delay } } }}>
      {children}
    </motion.div>
  );
};

const ICONS = [
  // template
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">
    <path d="M6 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6ZM15.75 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3H18a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-2.25ZM6 12.75a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3v-2.25a3 3 0 0 0-3-3H6ZM17.625 13.5a.75.75 0 0 0-1.5 0v2.625H13.5a.75.75 0 0 0 0 1.5h2.625v2.625a.75.75 0 0 0 1.5 0v-2.625h2.625a.75.75 0 0 0 0-1.5h-2.625V13.5Z" />
  </svg>,
  // photo
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">
    <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z" clipRule="evenodd" />
  </svg>,
  // personnaliser
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">
    <path d="M18.75 12.75h1.5a.75.75 0 0 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM12 6a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 6ZM12 18a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 18ZM3.75 6.75h1.5a.75.75 0 1 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM5.25 18.75h-1.5a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 0 1.5ZM3 12a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 3 12ZM9 3.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5ZM12.75 12a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0ZM9 15.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
  </svg>,
  // télécharger
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">
    <path d="M12 1.5a.75.75 0 0 1 .75.75V7.5h-1.5V2.25A.75.75 0 0 1 12 1.5ZM11.25 7.5v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V7.5h3.75a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9a3 3 0 0 1 3-3h3.75Z" />
  </svg>,
];

export default function Home() {
  const [siteLang, setSiteLang] = useState('FR');
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const content = {
    FR: {
      hero: 'CRÉEZ VOTRE COMPOSITE EN QUELQUES MINUTES',
      heroSub: 'L\'outil professionnel pour mannequins et comédiens. Choisissez un template, ajoutez vos photos, téléchargez en HD.',
      cta: 'CRÉER MON COMPOSITE GRATUITEMENT',
      ctaSub: 'Sans inscription · Sans carte bancaire · 100% gratuit',
      how: 'COMMENT ÇA MARCHE',
      steps: [
        { num: '01', title: 'Choisissez un template', desc: '8 mises en page professionnelles, recto et verso, adaptées aux standards des agences.' },
        { num: '02', title: 'Ajoutez vos photos', desc: 'Importez jusqu\'à 16 photos HD. Recadrez, zoomez, repositionnez en quelques clics.' },
        { num: '03', title: 'Personnalisez', desc: 'Mensurations, coordonnées, palette de couleurs. Votre composite, votre identité.' },
        { num: '04', title: 'Téléchargez', desc: 'Export PDF haute résolution format A5, prêt pour l\'impression ou l\'envoi.' },
      ],
      argTitle: 'POURQUOI COMPIFY ?',
      args: [
        { icon: '⚡', title: 'Rapide', desc: 'Votre composite prêt en moins de 5 minutes. Plus besoin de Photoshop.' },
        { icon: '✦', title: 'Professionnel', desc: 'Templates conçus selon les standards des agences internationales.' },
        { icon: '↓', title: 'Print-ready', desc: 'Export PDF HD format A5, prêt pour l\'impression professionnelle.' },
      ],
      freeTitle: 'COMPIFY EST 100% GRATUIT',
      freeSub: 'Financé par la publicité ciblée. Aucune carte bancaire, aucun abonnement, jamais.',
      freeArgs: [
        { icon: '∞', title: 'Gratuit pour toujours', desc: 'Pas de freemium caché, pas de limite cachée.' },
        { icon: '⊘', title: 'Sans inscription', desc: 'Créez votre composite immédiatement, sans compte.' },
        { icon: '🔒', title: 'Photos protégées', desc: 'Vos photos ne quittent jamais votre appareil.' },
      ],
      agenceTitle: 'VOUS ÊTES UNE AGENCE ?',
      agenceSub: 'Logo personnalisé, sans publicité, multi-comptes. Une solution dédiée aux professionnels.',
      agenceCta: 'NOUS CONTACTER',
      faqTitle: 'QUESTIONS FRÉQUENTES',
      faqs: [
        { q: 'Est-ce vraiment gratuit ?', a: 'Oui, Compify est 100% gratuit. Le service est financé par des publicités ciblées pertinentes pour les mannequins.' },
        { q: 'Quel format de sortie ?', a: 'PDF haute résolution au format A5 (148×210mm), idéal pour l\'impression et l\'envoi numérique.' },
        { q: 'Mes photos sont-elles stockées ?', a: 'Non, vos photos restent sur votre appareil. Elles ne sont jamais envoyées sur nos serveurs.' },
        { q: 'Puis-je créer autant de composites que je veux ?', a: 'Oui, sans limite. Créez, modifiez et téléchargez autant de composites que nécessaire.' },
        { q: 'Le composite est-il aux normes agences ?', a: 'Oui, le format A5 recto/verso est le standard international utilisé par les agences de mannequinat.' },
      ],
      finalCta: 'PRÊT À CRÉER VOTRE COMPOSITE ?',
      finalBtn: 'COMMENCER MAINTENANT',
    },
    EN: {
      hero: 'CREATE YOUR COMPOSITE IN MINUTES',
      heroSub: 'The professional tool for models and actors. Choose a template, add your photos, download in HD.',
      cta: 'CREATE MY COMPOSITE FOR FREE',
      ctaSub: 'No sign up · No credit card · 100% free',
      how: 'HOW IT WORKS',
      steps: [
        { num: '01', title: 'Choose a template', desc: '8 professional layouts, front and back, matching agency standards.' },
        { num: '02', title: 'Add your photos', desc: 'Import up to 16 HD photos. Crop, zoom, reposition with a few clicks.' },
        { num: '03', title: 'Customize', desc: 'Measurements, contact details, color palette. Your composite, your identity.' },
        { num: '04', title: 'Download', desc: 'High-resolution A5 PDF export, ready for printing or digital sharing.' },
      ],
      argTitle: 'WHY COMPIFY?',
      args: [
        { icon: '⚡', title: 'Fast', desc: 'Your composite ready in under 5 minutes. No Photoshop needed.' },
        { icon: '✦', title: 'Professional', desc: 'Templates designed to international agency standards.' },
        { icon: '↓', title: 'Print-ready', desc: 'HD PDF export in A5 format, ready for professional printing.' },
      ],
      freeTitle: 'COMPIFY IS 100% FREE',
      freeSub: 'Funded by targeted advertising. No credit card, no subscription, ever.',
      freeArgs: [
        { icon: '∞', title: 'Free forever', desc: 'No hidden freemium, no hidden limits.' },
        { icon: '⊘', title: 'No sign up', desc: 'Create your composite immediately, no account needed.' },
        { icon: '🔒', title: 'Photos protected', desc: 'Your photos never leave your device.' },
      ],
      agenceTitle: 'ARE YOU AN AGENCY?',
      agenceSub: 'Custom logo, no ads, multi-accounts. A dedicated solution for professionals.',
      agenceCta: 'CONTACT US',
      faqTitle: 'FREQUENTLY ASKED QUESTIONS',
      faqs: [
        { q: 'Is it really free?', a: 'Yes, Compify is 100% free. The service is funded by targeted ads relevant to models.' },
        { q: 'What export format?', a: 'High-resolution PDF in A5 format (148×210mm), ideal for printing and digital sharing.' },
        { q: 'Are my photos stored?', a: 'No, your photos stay on your device. They are never sent to our servers.' },
        { q: 'Can I create as many composites as I want?', a: 'Yes, unlimited. Create, edit and download as many composites as you need.' },
        { q: 'Is the composite agency-standard?', a: 'Yes, the A5 double-sided format is the international standard used by modeling agencies.' },
      ],
      finalCta: 'READY TO CREATE YOUR COMPOSITE?',
      finalBtn: 'START NOW',
    }
  };

  const d = content[siteLang];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ padding: '120px 24px 100px', textAlign: 'center', position: 'relative', overflow: 'hidden', minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/groupe-mannequins.jpg)', backgroundSize: 'cover', backgroundPosition: 'center top', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1 }} />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.02, 0.05, 0.02] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', borderRadius: '50%', backgroundColor: '#FF0066', pointerEvents: 'none', zIndex: 2 }}
        />
        <div style={{ maxWidth: '780px', margin: '0 auto', position: 'relative', zIndex: 3 }}>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 24px', fontWeight: '700' }}>
            COMPIFY
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            style={{ color: '#fff', fontSize: '52px', fontWeight: '900', letterSpacing: '2px', margin: '0 0 28px', lineHeight: 1.1, textTransform: 'uppercase' }}>
            {d.hero}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            style={{ color: '#888', fontSize: '17px', margin: '0 0 48px', lineHeight: 1.8 }}>
            {d.heroSub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
            <motion.button onClick={() => navigate('/editeur')}
              whileHover={{ scale: 1.04, backgroundColor: '#ff1a75' }} whileTap={{ scale: 0.97 }}
              style={{ backgroundColor: '#FF0066', color: '#fff', border: 'none', padding: '18px 48px', fontSize: '13px', letterSpacing: '3px', fontWeight: '700', cursor: 'pointer', marginBottom: '16px', display: 'inline-block' }}>
              {d.cta}
            </motion.button>
            <p style={{ color: '#555', fontSize: '11px', letterSpacing: '1px', margin: 0 }}>{d.ctaSub}</p>
          </motion.div>
        </div>
      </section>

      <AdSlot id="ad-home-hero" />

      {/* COMMENT CA MARCHE */}
      <section style={{ backgroundColor: '#f7f7f7', padding: '100px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <FadeSection>
            <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700', textAlign: 'center' }}>{d.how}</p>
            <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 auto 64px' }} />
          </FadeSection>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
            variants={stagger} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '32px' }}>
            {d.steps.map((step, i) => (
              <motion.div key={i} variants={fadeUp} style={{ textAlign: 'center' }}>
                {/* Icône SVG blanche dans carré fuchsia */}
                <div style={{ width: '52px', height: '52px', backgroundColor: '#FF0066', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  {ICONS[i]}
                </div>
                <p style={{ color: '#FF0066', fontSize: '40px', fontWeight: '900', margin: '0 0 20px', letterSpacing: '2px', lineHeight: 1 }}>{step.num}</p>
                <div style={{ width: '1px', height: '32px', backgroundColor: '#e0e0e0', margin: '0 auto 20px' }} />
                <p style={{ color: '#1A1A1A', fontSize: '13px', fontWeight: '700', margin: '0 0 12px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{step.title}</p>
                <p style={{ color: '#888', fontSize: '13px', lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ARGUMENTS */}
      <section style={{ backgroundColor: '#fff', padding: '100px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <FadeSection>
            <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700', textAlign: 'center' }}>{d.argTitle}</p>
            <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 auto 64px' }} />
          </FadeSection>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
            variants={stagger} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
            {d.args.map((arg, i) => (
              <motion.div key={i} variants={fadeUp}
                whileHover={{ y: -6, boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}
                style={{ textAlign: 'center', padding: '48px 32px', border: '1px solid #eee', transition: 'box-shadow 0.3s' }}>
                <p style={{ fontSize: '36px', margin: '0 0 20px' }}>{arg.icon}</p>
                <p style={{ color: '#1A1A1A', fontSize: '13px', fontWeight: '700', margin: '0 0 12px', letterSpacing: '2px', textTransform: 'uppercase' }}>{arg.title}</p>
                <p style={{ color: '#888', fontSize: '13px', lineHeight: 1.7, margin: 0 }}>{arg.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <AdSlot id="ad-home-pricing" />

      {/* 100% GRATUIT */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '100px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700' }}>{d.freeTitle}</p>
              <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 auto 20px' }} />
              <p style={{ color: '#666', fontSize: '15px', margin: 0, lineHeight: 1.7 }}>{d.freeSub}</p>
            </div>
          </FadeSection>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
            variants={stagger} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: '#2a2a2a', marginBottom: '64px' }}>
            {d.freeArgs.map((arg, i) => (
              <motion.div key={i} variants={fadeUp}
                style={{ backgroundColor: '#0e0e0e', padding: '48px 32px', textAlign: 'center' }}>
                <p style={{ color: '#FF0066', fontSize: '32px', margin: '0 0 20px', lineHeight: 1 }}>{arg.icon}</p>
                <p style={{ color: '#fff', fontSize: '13px', fontWeight: '700', margin: '0 0 12px', letterSpacing: '2px', textTransform: 'uppercase' }}>{arg.title}</p>
                <p style={{ color: '#666', fontSize: '13px', lineHeight: 1.7, margin: 0 }}>{arg.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <FadeSection>
            <motion.div
              whileHover={{ borderColor: '#FF0066' }}
              style={{ border: '1px solid #2a2a2a', padding: '40px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'border-color 0.3s' }}>
              <div>
                <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px', letterSpacing: '2px', textTransform: 'uppercase' }}>{d.agenceTitle}</p>
                <p style={{ color: '#666', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>{d.agenceSub}</p>
              </div>
              <motion.button onClick={() => navigate('/contact')}
                whileHover={{ scale: 1.04, backgroundColor: '#ff1a75' }} whileTap={{ scale: 0.97 }}
                style={{ backgroundColor: '#FF0066', color: '#fff', border: 'none', padding: '14px 32px', fontSize: '11px', letterSpacing: '3px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, marginLeft: '32px' }}>
                {d.agenceCta}
              </motion.button>
            </motion.div>
          </FadeSection>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: '#f7f7f7', padding: '100px 24px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <FadeSection>
            <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700', textAlign: 'center' }}>{d.faqTitle}</p>
            <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 auto 64px' }} />
          </FadeSection>
          {d.faqs.map((faq, i) => (
            <FadeSection key={i} delay={i * 0.05}>
              <div onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer', overflow: 'hidden' }}>
                <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: '#1A1A1A', fontSize: '15px', fontWeight: '700', margin: 0 }}>{faq.q}</p>
                  <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} transition={{ duration: 0.2 }}
                    style={{ color: '#FF0066', fontSize: '24px', fontWeight: '300', flexShrink: 0, marginLeft: '16px' }}>+</motion.span>
                </div>
                <motion.div initial={false}
                  animate={{ height: openFaq === i ? 'auto' : 0, opacity: openFaq === i ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}>
                  <p style={{ color: '#777', fontSize: '14px', lineHeight: 1.8, margin: '0 0 24px', paddingRight: '32px' }}>{faq.a}</p>
                </motion.div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section style={{ backgroundColor: '#FF0066', padding: '80px 24px', textAlign: 'center' }}>
        <FadeSection>
          <h2 style={{ color: '#fff', fontSize: '36px', fontWeight: '900', letterSpacing: '3px', margin: '0 0 32px', textTransform: 'uppercase' }}>
            {d.finalCta}
          </h2>
          <motion.button onClick={() => navigate('/editeur')}
            whileHover={{ scale: 1.04, backgroundColor: '#1A1A1A' }} whileTap={{ scale: 0.97 }}
            style={{ backgroundColor: '#fff', color: '#FF0066', border: 'none', padding: '18px 48px', fontSize: '13px', letterSpacing: '3px', fontWeight: '900', cursor: 'pointer' }}>
            {d.finalBtn}
          </motion.button>
        </FadeSection>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}